// reCAPTCHA v3: no widget, no challenge. The script is loaded on first use so
// pages that never submit a form do not pay for it, and every token is minted
// for a named action so the backend can refuse one made for something else.
const SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || "";
const SCRIPT_ID = "recaptcha-v3-script";

type Grecaptcha = {
  ready: (callback: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
};

declare global {
  interface Window {
    grecaptcha?: Grecaptcha;
  }
}

let loading: Promise<void> | null = null;

export const loadRecaptcha = (): Promise<void> => {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("reCAPTCHA needs a browser"));
  }

  if (!SITE_KEY) {
    return Promise.reject(new Error("reCAPTCHA site key is not configured"));
  }

  if (window.grecaptcha) {
    return Promise.resolve();
  }

  if (!loading) {
    loading = new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.src = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        // Let the next attempt try again instead of caching the failure.
        loading = null;
        script.remove();
        reject(new Error("reCAPTCHA could not be loaded"));
      };
      document.head.appendChild(script);
    });
  }

  return loading;
};

export const getCaptchaToken = async (action: string): Promise<string> => {
  await loadRecaptcha();

  const grecaptcha = window.grecaptcha;

  if (!grecaptcha) {
    throw new Error("reCAPTCHA is not available");
  }

  await new Promise<void>((resolve) => grecaptcha.ready(resolve));

  return grecaptcha.execute(SITE_KEY, { action });
};
